import React from "react";
import {Button, ButtonGroup, FormControlLabel, Grid, Switch} from "@mui/material";
import {observer} from "mobx-react-lite";
import {useStores} from "../../../stores";
import './KoiKeyDataFacts.scss';
import {KoiFacade} from "../../../components/koi-facade/KoiFacade";
import {toCurrency} from "../../../components/currency-component/CurrencyClasses";
import Typography from "@mui/material/Typography";
import {Bag} from "../../../services/classes/Bag";
import {
    CollapsibleIndentTree,
    FilterSpecification,
    Options,
    TreeDataType
} from "../../../components/visualization/collapsible-indent-tree/CollapsibleIndentTree";
import {FilteredSankeyComponent} from "./FilteredSankeyComponent";
import {Subject} from "rxjs";
import {UNCATEGORIZED_LABEL} from "../../../constants";
import * as d3 from "d3";
import {MatKpiTreeData} from "../../../services/ApiTypes";
import {useLocation} from "react-router";
import {generateHomePath} from "../../../routing/routing.utils";
import {DownloadReviewButtons} from "./DownloadReviewButtons";

export const KoiKeyDataFacts: React.FC = observer(() => {
    const {rootAppStore, bagStore, p} = useStores();
    const location = useLocation();
    const bagId = bagStore.bagId;
    const bag = bagStore.bag as Bag;

    const [filter, setFilter] = React.useState<any | undefined>(0);
    const hiddenRoot = false;
    const [hiddenUncat, setHiddenUncat] = React.useState<boolean>(true);

    const $filter = React.useMemo<FilterSpecification>(() => ({
        byLevel: new Subject<number>(),
    }), []);

    const options = React.useMemo<Partial<Options<MatKpiTreeData>> | undefined>(() => $filter ? {
        height: p.p.exploreCategoriesHeight ?? 5800,
        width: 720,
        $filter,
        columns: [
            {
                columnTitle: 'Total spend',
                getValue: (d, c) => {
                    return String(c.hiddenRoot && d.depth === 0 ? '' : toCurrency(p.currencySymbol, d.data.values.spend));
                },
                xOffset: 540,
            },
            {
                columnTitle: 'Suppliers',
                getValue: (d, c) =>
                    String(c.hiddenRoot && d.depth === 0 ? '' : (d.data.values.distinct_suppliers || '-')),
                xOffset: 610,
            },
            {
                columnTitle: 'Transactions',
                getValue: (d, c) =>
                    String(c.hiddenRoot && d.depth === 0 ? '' : (d.data.values.parts || '-')),
                xOffset: 700,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
    } : undefined, [$filter])

    // const clickContinue = () => {
    //     jobStore.setStep(2);
    //     // // Start enrichment
    //     // const oldJob = job;
    //     // loadingBar.start(true);
    //     // // Note: skipped enriching for now
    //     // JobService.startJobCategorization(oldJob.id).then(nextJob => {
    //     //     jobStore.setJob(nextJob);
    //     // }).finally(() => {
    //     //     loadingBar.setComplete();
    //     // })
    // };

    return <KoiFacade
        title="Explore Categories"
        // helpContent={<PurchasePriceVarianceHelp/>}
        intro={<></>}
        back={generateHomePath(location, {id: String(bagId)}, rootAppStore.app)} // Deprecated
        className="koi-kdf-page"
    >
        {p.p.enableSankey &&
            <div className="page-item sankey-item">
                <FilteredSankeyComponent/>
            </div>
        }

        {bagStore.kpi.taxonomyData && <>
            <section className="page-item taxonomy-tree-item">
                <header className="taxonomy-tree-header">
                    <Grid container>
                        <Grid item xs={4}></Grid>
                        <Grid item xs={4}>
                            <Typography variant="h3" component="h2">
                                Category overview
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <DownloadReviewButtons/>
                        </Grid>
                    </Grid>
                    <div className="buttons">
                        <ButtonGroup className="mx-4">
                            {[...Array(bag.taxonomy_size)].map((_, i) =>
                                <Button
                                    key={i}
                                    color="primary"
                                    onClick={() => {
                                        // bugfix when pressing L3->L1->L2
                                        ($filter.byLevel as Subject<number>).next(i);
                                        ($filter.byLevel as Subject<number>).next(i);
                                    }}
                                    variant="outlined">
                                    L{String(i + 1)}
                                </Button>
                            )}
                        </ButtonGroup>
                        <ButtonGroup className="mx-4">
                            <Button onClick={() => setFilter(0)} variant={filter === 0 ? 'contained' : 'outlined'}
                                    color="primary">
                                No filter
                            </Button>
                            <Button onClick={() => setFilter(1)} variant={filter === 1 ? 'contained' : 'outlined'}
                                    color="primary">
                                &gt;= {toCurrency(p.currencySymbol, 1_000)}
                            </Button>
                            <Button onClick={() => setFilter(2)} variant={filter === 2 ? 'contained' : 'outlined'}
                                    color="primary">
                                &gt;= {toCurrency(p.currencySymbol, 1_000_000)}
                            </Button>
                            <Button onClick={() => setFilter(3)} variant={filter === 3 ? 'contained' : 'outlined'}
                                    color="primary">
                                &gt;= 5 sup
                            </Button>
                            <Button onClick={() => setFilter(4)} variant={filter === 4 ? 'contained' : 'outlined'}
                                    color="primary">
                                &gt;= 100 transactions
                            </Button>
                        </ButtonGroup>
                        {/*<Button onClick={() => setHiddenRoot(!hiddenRoot)} variant="outlined"*/}
                        {/*        color="primary" className="ml-4">*/}
                        {/*    {hiddenRoot ? 'Show total' : 'Hide total'}*/}
                        {/*</Button>*/}

                        <FormControlLabel
                            className="mx-4"
                            control={<Switch size="small"
                                             checked={!hiddenUncat}
                                             onChange={() => setHiddenUncat(!hiddenUncat)}/>}
                            label={UNCATEGORIZED_LABEL}
                        />
                    </div>
                </header>
                <Grid container justifyContent="center">
                    <Grid item xs={7}>
                        <CollapsibleIndentTree
                            data={bagStore.kpi.taxonomyData}
                            options={options}
                            filter={filter}
                            hiddenRoot={hiddenRoot}
                            hiddenUncat={hiddenUncat}
                        />
                    </Grid>
                </Grid>
            </section>
        </>}
    </KoiFacade>
});

type Filter = undefined | ((d: d3.HierarchyNode<TreeDataType<MatKpiTreeData>>) => boolean);
export const CDT_FILTERS: Filter[] = [
    undefined,
    d => d?.data?.values.spend >= 1_000,
    d => d?.data?.values.spend >= 1_000_000,
    d => d?.data?.values.distinct_suppliers >= 5,
    d => d?.data?.values.distinct_suppliers >= 100,
]
